import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, X } from 'lucide-react';
import { Meteo } from './Meteo';
const galleryImages = ['https://cdn.magicpatterns.com/uploads/3MJ8gtBUs25W4L6qMfTGhG/LKHV_pano.jpg', 'https://cdn.magicpatterns.com/uploads/iozdfqDTeWAMv6eWPB2rYx/RWY06.jpg', 'https://cdn.magicpatterns.com/uploads/63p7bgLJTz6Dx1wKnSn3mB/obr01.jpg'];
export function Hero() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);
  return <section id="hero" className="relative h-screen w-full overflow-hidden flex flex-col items-center justify-center" data-id="element-114">
      {/* Background with Gradient */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#0a1628] via-[#1e3a5f] to-[#0f1d35]" data-id="element-115">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1/2 bg-[#38bdf8] opacity-10 blur-[120px] rounded-full pointer-events-none" data-id="element-116" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto mb-auto pt-[28vh]" data-id="element-117">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-118">
          <h1 className="text-8xl md:text-9xl font-bold text-[#38bdf8] tracking-widest mb-2" data-id="element-119">
            LKHV
          </h1>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} data-id="element-120">
          <h2 className="text-xl md:text-3xl text-white font-light tracking-[0.3em] uppercase mb-2" style={{
          fontSize: '28px'
        }} data-id="element-121">
            Letiště hořovice
          </h2>
          <p className="text-white/60 text-lg uppercase tracking-widest mb-8" data-id="element-122">
            Neveřejné vnitrostátní letiště
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.6
      }} className="inline-block border border-white/10 bg-white/5 backdrop-blur-sm rounded-full px-6 py-2" data-id="element-123">
          <p className="text-sm md:text-base text-white/60 font-mono tracking-wider" data-id="element-124">
            N 49°50'41" E 013°53'09"
          </p>
        </motion.div>
      </div>

      {/* Meteo Section */}
      <Meteo data-id="element-125" />

      {/* Gallery Thumbnails */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.8
    }} className="relative z-10 w-full max-w-7xl mx-auto px-6 mb-20" data-id="element-126">
        <div className="grid grid-cols-3" style={{
        gap: '30px'
      }} data-id="element-127">
          {galleryImages.map((src, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.9 + index * 0.1
        }} className="relative h-28 md:h-36 overflow-hidden rounded-lg cursor-pointer group" onClick={() => setSelectedImage(src)} data-id="element-128">
              <img src={src} alt={`Letiště Hořovice ${index + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-129" />
              <div className="absolute inset-0 bg-[#1f3f65]/70 transition-opacity duration-300 group-hover:opacity-0" data-id="element-130" />
            </motion.div>)}
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.2,
      duration: 1
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10" data-id="element-131">
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} data-id="element-132">
          <ChevronDown className="text-white/50 w-8 h-8" data-id="element-133" />
        </motion.div>
      </motion.div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-134">
        {selectedImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-8" onClick={() => setSelectedImage(null)} data-id="element-135">
            <button className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-50 bg-black/20 p-2 rounded-full backdrop-blur-md" onClick={e => {
          e.stopPropagation();
          setSelectedImage(null);
        }} data-id="element-136">
              <X size={32} data-id="element-137" />
            </button>
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="relative max-w-7xl max-h-[90vh] w-full flex items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-138">
              <img src={selectedImage} alt="Enlarged view" className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" data-id="element-139" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}